import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, Search, ChevronDown, User } from "lucide-react";
import { CATEGORIES, CATEGORY_LABELS, SUBCATEGORIES, SUBCATEGORY_LABELS } from "../lib/constants";
import { useBrand } from "../context/BrandContext";
import { useAuth } from "../context/AuthContext";

// Left-locked masthead with a hard 2px black baseline. Sections are numbered,
// evenly tracked and sit flush left — the grid is the ornament.
export function Navbar() {
    const brand = useBrand();
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [q, setQ] = useState("");

    const doSearch = (e) => {
        e.preventDefault();
        if (q.trim()) {
            navigate(`/search?q=${encodeURIComponent(q.trim())}`);
            setSearchOpen(false);
            setMobileOpen(false);
            setQ("");
        }
    };

    return (
        <header className="sticky top-0 z-50 glass border-b-2 border-foreground" data-testid="navbar">
            <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-[72px] gap-6">
                    <button
                        className="lg:hidden p-2 -ml-2 text-foreground"
                        onClick={() => setMobileOpen((v) => !v)}
                        aria-label="Toggle menu"
                        data-testid="mobile-menu-toggle"
                    >
                        {mobileOpen ? <X size={22} /> : <Menu size={22} />}
                    </button>

                    <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="navbar-logo">
                        <span className="w-5 h-5 bg-accent shrink-0" />
                        <span className="font-display text-xl sm:text-2xl font-black uppercase tracking-[-0.03em] leading-none">
                            {brand.site_name}
                        </span>
                    </Link>

                    <div className="flex items-center gap-5">
                        <button
                            onClick={() => setSearchOpen((v) => !v)}
                            className="p-1 text-foreground hover:text-accent transition-colors duration-100"
                            aria-label="Search"
                            data-testid="navbar-search-toggle"
                        >
                            <Search size={19} />
                        </button>
                        {user ? (
                            <div className="hidden sm:flex items-center gap-5">
                                <Link
                                    to="/account"
                                    data-testid="navbar-account"
                                    className="flex items-center gap-1.5 eyebrow hover:text-accent transition-colors duration-100"
                                >
                                    <User size={14} /> {user.name?.split(" ")[0]}
                                </Link>
                                <button
                                    onClick={() => { logout(); navigate("/"); }}
                                    data-testid="navbar-logout"
                                    className="eyebrow hover:text-accent transition-colors duration-100"
                                >
                                    Log out
                                </button>
                            </div>
                        ) : (
                            <div className="hidden sm:flex items-center gap-4">
                                <Link
                                    to="/login"
                                    data-testid="navbar-login"
                                    className="eyebrow hover:text-accent transition-colors duration-100"
                                >
                                    Log in
                                </Link>
                                <Link
                                    to="/register"
                                    data-testid="navbar-register"
                                    className="eyebrow bg-accent text-accent-foreground px-5 py-2.5 hover:bg-foreground transition-colors duration-100"
                                >
                                    Subscribe
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Numbered section index */}
            <nav className="hidden lg:block border-t border-line">
                <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
                    <ul className="flex items-stretch">
                        {CATEGORIES.map((cat, i) => (
                            <li key={cat} className="relative group border-r border-line first:border-l">
                                <Link
                                    to={`/category/${cat}`}
                                    data-testid={`nav-cat-${cat}`}
                                    className="flex items-center gap-2 px-5 py-3.5 eyebrow hover:bg-accent hover:text-accent-foreground transition-colors duration-100"
                                >
                                    <span className="text-accent group-hover:text-accent-foreground tabular-nums">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    {CATEGORY_LABELS[cat]}
                                    {cat === "personal-finance" && <ChevronDown size={12} />}
                                </Link>
                                {cat === "personal-finance" && (
                                    <div className="absolute left-0 top-full w-60 invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-opacity duration-100 z-50">
                                        <div className="bg-surface border-2 border-foreground shadow-lift">
                                            {SUBCATEGORIES.map((sub) => (
                                                <Link
                                                    key={sub}
                                                    to={`/category/personal-finance/${sub}`}
                                                    data-testid={`nav-sub-${sub}`}
                                                    className="block px-5 py-3 eyebrow border-b border-line last:border-0 hover:bg-accent hover:text-accent-foreground transition-colors duration-100"
                                                >
                                                    {SUBCATEGORY_LABELS[sub]}
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </li>
                        ))}
                        <li className="ml-auto flex">
                            <Link
                                to="/archive"
                                data-testid="nav-archive"
                                className="flex items-center px-5 py-3.5 eyebrow text-dim hover:text-accent transition-colors duration-100"
                            >
                                Archive
                            </Link>
                        </li>
                    </ul>
                </div>
            </nav>

            {/* Search panel */}
            {searchOpen && (
                <div className="border-t border-line bg-surface-sunken" data-testid="navbar-search-panel">
                    <form onSubmit={doSearch} className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-5 flex gap-3">
                        <input
                            autoFocus
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search articles, markets, authors..."
                            data-testid="navbar-search-input"
                            className="field"
                        />
                        <button
                            type="submit"
                            data-testid="navbar-search-submit"
                            className="eyebrow bg-foreground text-canvas px-8 hover:bg-accent transition-colors duration-100"
                        >
                            Search
                        </button>
                    </form>
                </div>
            )}

            {/* Mobile menu */}
            {mobileOpen && (
                <nav className="lg:hidden border-t border-line bg-surface" data-testid="mobile-menu">
                    <ul>
                        {CATEGORIES.map((cat, i) => (
                            <li key={cat} className="border-b border-line">
                                <Link
                                    to={`/category/${cat}`}
                                    onClick={() => setMobileOpen(false)}
                                    className="flex items-center gap-3 px-6 py-3.5 eyebrow"
                                >
                                    <span className="text-accent tabular-nums">{String(i + 1).padStart(2, "0")}</span>
                                    {CATEGORY_LABELS[cat]}
                                </Link>
                            </li>
                        ))}
                        <li className="border-b border-line">
                            <Link to="/archive" onClick={() => setMobileOpen(false)} className="block px-6 py-3.5 eyebrow text-dim">
                                Archive
                            </Link>
                        </li>
                        {!user && (
                            <li className="flex gap-3 p-5">
                                <Link to="/login" onClick={() => setMobileOpen(false)} className="flex-1 text-center eyebrow border-2 border-foreground py-3">
                                    Log in
                                </Link>
                                <Link to="/register" onClick={() => setMobileOpen(false)} className="flex-1 text-center eyebrow bg-accent text-accent-foreground py-3">
                                    Subscribe
                                </Link>
                            </li>
                        )}
                        {user && (
                            <li className="p-5 space-y-3">
                                <Link to="/account" onClick={() => setMobileOpen(false)} data-testid="mobile-account" className="block text-center eyebrow border-2 border-foreground py-3">
                                    My account
                                </Link>
                                <button onClick={() => { logout(); setMobileOpen(false); navigate("/"); }} className="w-full eyebrow border-2 border-foreground py-3">
                                    Log out
                                </button>
                            </li>
                        )}
                    </ul>
                </nav>
            )}
        </header>
    );
}
